import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);
const AUTH_STORAGE_KEY = 'womio_auth_user';

export function AuthProvider({ children }) {
  // Initialize from sessionStorage if available (survives navigation)
  const [user, setUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem(AUTH_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (err) {
      console.error('AuthProvider init: failed to parse sessionStorage', err);
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
      sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(res.data));
    } catch {
      setUser(null);
      sessionStorage.removeItem(AUTH_STORAGE_KEY);
    } finally {
      setLoading(false);
      setAuthReady(true);
    }
  }, []);

  useEffect(() => {
    // On /auth/callback route, don't check /me — AuthCallback handles it
    if (window.location.pathname === '/auth/callback') {
      // If we already have user from sessionStorage, mark as ready
      if (user) {
        setLoading(false);
        setAuthReady(true);
      } else {
        setLoading(false);
      }
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback((loginType = 'user') => {
    sessionStorage.setItem('womio_login_type', loginType);
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout request failed', err);
    }
    setUser(null);
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  }, []);

  // Called by AuthCallback after successful session exchange
  const completeAuth = useCallback((userData) => {
    setUser(userData);
    setAuthReady(true);
    setLoading(false);
    // Persist to sessionStorage so it survives navigation
    try {
      sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
    } catch (err) {
      console.error('Failed to persist user to sessionStorage', err);
    }
    // Login responses don't carry admin permissions: refresh from /auth/me (keep user on failure).
    axios.get(`${API}/auth/me`, { withCredentials: true }).then((res) => {
      setUser(res.data);
      sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(res.data));
    }).catch(() => {
      setUser((u) => (u && u.admin_permissions === undefined ? { ...u, admin_permissions: [] } : u));
    });
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, authReady, login, logout, checkAuth, completeAuth }),
    [user, loading, authReady, login, logout, checkAuth, completeAuth]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
